import jsPDF from 'jspdf';
import type { JoSAAFormData, JoSAAPrediction } from './josaaService';

interface JoSAAReportData {
  studentInfo: JoSAAFormData;
  predictions: JoSAAPrediction[];
  paymentInfo: {
    amountPaid: number;
    promoCode: string | null;
    discount: number;
  };
}

export const generateJoSAAPDF = async (data: JoSAAReportData): Promise<void> => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  
  // Define colors
  const colors = {
    primary: [59, 130, 246],    // Blue
    secondary: [107, 114, 128], // Gray
    success: [34, 197, 94],     // Green
    warning: [245, 158, 11],    // Amber
    danger: [239, 68, 68],      // Red
    light: [248, 250, 252],     // Light gray
    dark: [31, 41, 55]          // Dark gray
  };
  
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  
  // Set default font
  pdf.setFont('helvetica', 'normal');
  
  // Function to add a new page with header
  const addNewPage = (pageTitle = '') => {
    pdf.addPage();
    
    // Add header
    pdf.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
    pdf.rect(0, 0, pageWidth, 25, 'F');
    
    // Add logo placeholder
    pdf.setFillColor(255, 255, 255);
    pdf.circle(15, 12.5, 8, 'F');
    pdf.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('GV', 12, 15);
    
    // Add title
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(14);
    pdf.text('GRAD VIKALP - JoSAA COLLEGE PREDICTIONS', 30, 15);
    
    if (pageTitle) {
      pdf.setFontSize(12);
      pdf.text(pageTitle, pageWidth - margin - pdf.getTextWidth(pageTitle), 15);
    }
    
    // Add footer
    pdf.setFillColor(colors.light[0], colors.light[1], colors.light[2]);
    pdf.rect(0, pageHeight - 15, pageWidth, 15, 'F');
    
    pdf.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
    pdf.setFontSize(8);
    pdf.text(`Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, margin, pageHeight - 5);
    pdf.text(`Page ${pdf.getNumberOfPages()}`, pageWidth - margin - 15, pageHeight - 5);
    
    return margin + 30; // Return starting Y position after header
  };
  
  // Helper function to add new page if needed
  const checkPageBreak = (requiredHeight: number) => {
    if (yPosition + requiredHeight > pageHeight - 25) {
      yPosition = addNewPage();
      return true;
    }
    return false;
  };

  // Create cover page
  let yPosition = margin + 20;
  
  // Cover page header
  pdf.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
  pdf.rect(0, 0, pageWidth, 80, 'F');
  
  // Logo
  pdf.setFillColor(255, 255, 255);
  pdf.circle(pageWidth/2, 40, 20, 'F');
  pdf.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.text('GV', pageWidth/2 - 10, 47);
  
  // Cover page title
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(28);
  pdf.setFont('helvetica', 'bold');
  pdf.text('JoSAA COLLEGE', pageWidth/2, 90, { align: 'center' });
  pdf.text('PREDICTION REPORT', pageWidth/2, 100, { align: 'center' });
  
  yPosition = 120;
  
  // Student info box
  pdf.setFillColor(colors.light[0], colors.light[1], colors.light[2]);
  pdf.roundedRect(margin, yPosition, contentWidth, 90, 5, 5, 'F');
  
  pdf.setTextColor(colors.dark[0], colors.dark[1], colors.dark[2]);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('STUDENT INFORMATION', margin + 10, yPosition + 15);
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  
  // Format student info in two columns
  const studentInfoLeft = [
    `Name: ${data.studentInfo.name}`,
    `Email: ${data.studentInfo.email}`,
    `JEE Main CRL: ${data.studentInfo.jeeMainCRL}`,
    `Category: ${data.studentInfo.category}`
  ];
  
  const studentInfoRight = [
    `Gender: ${data.studentInfo.gender}`,
    `Home State: ${data.studentInfo.homeState}`,
    ''
  ];

  if (data.studentInfo.jeeAdvancedQualified) {
    studentInfoRight[2] = `JEE Advanced: ${data.studentInfo.jeeAdvancedRank}`;
  }
  
  // Left column
  let infoY = yPosition + 30;
  studentInfoLeft.forEach(info => {
    pdf.text(info, margin + 15, infoY);
    infoY += 12;
  });
  
  // Right column
  infoY = yPosition + 30;
  studentInfoRight.forEach(info => {
    if (info) {
      pdf.text(info, margin + contentWidth/2, infoY);
    }
    infoY += 12;
  });
  
  yPosition = 230;
  
  // Payment info box
  pdf.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2], 0.1);
  pdf.roundedRect(margin, yPosition, contentWidth, 50, 5, 5, 'F');
  
  pdf.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('PAYMENT INFORMATION', margin + 10, yPosition + 15);
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Amount Paid: ₹${data.paymentInfo.amountPaid}`, margin + 15, yPosition + 30);
  
  if (data.paymentInfo.promoCode) {
    pdf.text(`Promo Code: ${data.paymentInfo.promoCode} (₹${data.paymentInfo.discount} discount)`, margin + contentWidth/2, yPosition + 30);
  }
  
  
  // Summary box
  yPosition = 300;
  pdf.setFillColor(colors.success[0], colors.success[1], colors.success[2], 0.1);
  pdf.roundedRect(margin, yPosition, contentWidth, 60, 5, 5, 'F');
  
  pdf.setTextColor(colors.success[0], colors.success[1], colors.success[2]);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('JoSAA PREDICTION SUMMARY', margin + 10, yPosition + 15);
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Total Predictions: ${data.predictions.length} colleges`, margin + 15, yPosition + 35);
  
  // Statistics by institute type
  const instituteStats = {
    IIT: data.predictions.filter(p => p.institute.institute_type === 'IIT').length,
    NIT: data.predictions.filter(p => p.institute.institute_type === 'NIT').length,
    IIIT: data.predictions.filter(p => p.institute.institute_type === 'IIIT').length,
    GFTI: data.predictions.filter(p => p.institute.institute_type === 'GFTI').length
  };

  const boxWidth = contentWidth / 4 - 5;
  
  // Institute type boxes
  Object.entries(instituteStats).forEach(([type, count], index) => {
    const xPos = margin + 5 + (boxWidth + 5) * index;
    
    pdf.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
    pdf.roundedRect(xPos, yPosition + 45, boxWidth, 25, 3, 3, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    pdf.text(type, xPos + boxWidth/2, yPosition + 55, { align: 'center' });
    pdf.text(`${count}`, xPos + boxWidth/2, yPosition + 65, { align: 'center' });
  });
  
  // Helper function to add prediction section
  const addPredictionSection = (title: string, predictions: JoSAAPrediction[], color: number[]) => {
    if (predictions.length === 0) return;
    
    // Add a new page for each section
    yPosition = addNewPage(`${title} PREDICTIONS`);
    
    // Section header
    pdf.setFillColor(color[0], color[1], color[2], 0.2);
    pdf.roundedRect(margin, yPosition - 5, contentWidth, 15, 3, 3, 'F');
    
    pdf.setTextColor(color[0], color[1], color[2]);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${title} PREDICTIONS (${predictions.length})`, margin + 10, yPosition + 5);
    
    yPosition += 20;
    pdf.setTextColor(colors.dark[0], colors.dark[1], colors.dark[2]);
    
    predictions.forEach((prediction, index) => {
      checkPageBreak(35);
      
      // Prediction card
      pdf.setFillColor(255, 255, 255);
      pdf.setDrawColor(color[0], color[1], color[2], 0.3);
      pdf.roundedRect(margin, yPosition - 5, contentWidth, 70, 3, 3, 'FD');
      
      // Institute name and type
      pdf.setTextColor(colors.dark[0], colors.dark[1], colors.dark[2]);
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${index + 1}. ${prediction.institute.name}`, margin + 5, yPosition + 5);
      
      pdf.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
      pdf.setFontSize(11);
      pdf.text(`${prediction.branch.branch_name} (${prediction.branch.branch_code})`, margin + 5, yPosition + 15);
      
      // Institute type badge
      pdf.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2], 0.2);
      pdf.roundedRect(margin + 5, yPosition + 20, 25, 8, 2, 2, 'F');
      pdf.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(prediction.institute.institute_type, margin + 17.5, yPosition + 26, { align: 'center' });
      
      // Admission probability badge
      const probabilityText = `${prediction.probabilityPercentage}% Chance`;
      const probabilityWidth = pdf.getTextWidth(probabilityText) + 10;
      
      if (prediction.probabilityPercentage >= 70) {
        pdf.setFillColor(colors.success[0], colors.success[1], colors.success[2], 0.2);
        pdf.setTextColor(colors.success[0], colors.success[1], colors.success[2]);
      } else if (prediction.probabilityPercentage >= 40) {
        pdf.setFillColor(colors.warning[0], colors.warning[1], colors.warning[2], 0.2);
        pdf.setTextColor(colors.warning[0], colors.warning[1], colors.warning[2]);
      } else {
        pdf.setFillColor(colors.danger[0], colors.danger[1], colors.danger[2], 0.2);
        pdf.setTextColor(colors.danger[0], colors.danger[1], colors.danger[2]);
      }
      
      pdf.roundedRect(pageWidth - margin - probabilityWidth - 5, yPosition, probabilityWidth, 10, 3, 3, 'F');
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text(probabilityText, pageWidth - margin - probabilityWidth/2 - 5, yPosition + 7, { align: 'center' });
      
      // Prediction details
      pdf.setTextColor(colors.dark[0], colors.dark[1], colors.dark[2]);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      
      // Left column
      pdf.text(`Location: ${prediction.institute.location}, ${prediction.institute.state}`, margin + 10, yPosition + 40);
      pdf.text(`Your Rank: ${prediction.userRank} (${prediction.examType})`, margin + 10, yPosition + 50);
      pdf.text(`Degree: ${prediction.branch.degree_type} (${prediction.branch.duration})`, margin + 10, yPosition + 60);
      
      // Right column
      pdf.text(`Closing Rank: ${prediction.cutoff.closing_rank}`, margin + contentWidth/2, yPosition + 40);
      pdf.text(`Round: ${prediction.cutoff.round_name} (${prediction.cutoff.year})`, margin + contentWidth/2, yPosition + 50);
      pdf.text(`Quota: ${prediction.cutoff.quota}`, margin + contentWidth/2, yPosition + 60);
      
      yPosition += 65;
      
      // Additional round info
      if (prediction.cutoff) {
        pdf.setFillColor(colors.light[0], colors.light[1], colors.light[2]);
        pdf.roundedRect(margin, yPosition - 5, contentWidth, 20, 3, 3, 'F');
        
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
        pdf.text('Selected Round Details:', margin + 10, yPosition + 5);
        
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(colors.dark[0], colors.dark[1], colors.dark[2]);
        pdf.text(`${prediction.cutoff.round_name} - Year: ${prediction.cutoff.year} - Seats: ${prediction.cutoff.seats_available || 'N/A'}`, 
          margin + contentWidth/2, yPosition + 5);
        
        yPosition += 25;
      } else {
        yPosition += 5;
      }
    });
    
    yPosition += 5;
  };
  
  // Categorize predictions by chance
  // Group by probability ranges for better organization
  const highChance = data.predictions.filter(p => p.probabilityPercentage >= 70);
  const mediumChance = data.predictions.filter(p => p.probabilityPercentage >= 40 && p.probabilityPercentage < 70);
  const lowChance = data.predictions.filter(p => p.probabilityPercentage < 40);
  
  // Add prediction sections
  addPredictionSection('HIGH PROBABILITY (70%+)', highChance, [34, 197, 94]);
  addPredictionSection('MEDIUM PROBABILITY (40-69%)', mediumChance, [245, 158, 11]);
  addPredictionSection('LOW PROBABILITY (<40%)', lowChance, [239, 68, 68]);
  
  // Add choice filling strategy page
  yPosition = addNewPage('CHOICE FILLING STRATEGY');
  
  pdf.setTextColor(colors.dark[0], colors.dark[1], colors.dark[2]);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('JoSAA CHOICE FILLING STRATEGY', pageWidth/2, yPosition, { align: 'center' });
  
  yPosition += 20;
  
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  
  const strategyGuide = [
    "1. FILL ALL AVAILABLE CHOICES",
    "   - Use all 200+ choices available in JoSAA",
    "   - More choices = better chances of getting a seat",
    "",
    "2. PRIORITIZE BY PREFERENCE",
    "   - List your most preferred colleges first",
    "   - Consider branch preference over institute ranking",
    "",
    "3. INCLUDE SAFETY OPTIONS",
    "   - Add colleges where you have high admission probability",
    "   - Ensure you get a seat somewhere",
    "",
    "4. CONSIDER LOCATION",
    "   - Factor in distance from home",
    "   - Consider cost of living in different cities",
    "",
    "5. RESEARCH THOROUGHLY",
    "   - Check placement records and faculty",
    "   - Visit college websites and talk to alumni",
    "",
    "6. ROUND-WISE STRATEGY",
    "   - Cutoffs may vary across rounds",
    "   - Don't skip rounds - participate in all",
    "",
    "IMPORTANT REMINDERS:",
    "- Seat allocation is automatic based on your choice order",
    "- You cannot change choices after submission",
    "- Accept allocated seat to secure admission",
    "- Participate in all counseling rounds"
  ];
  
  let textY = yPosition;
  strategyGuide.forEach(line => {
    if (line === "") {
      textY += 8;
    } else if (line.startsWith("   ")) {
      pdf.setFont('helvetica', 'normal');
      pdf.text(line, margin + 20, textY);
      textY += 8;
    } else if (line.includes(":")) {
      pdf.setFont('helvetica', 'bold');
      pdf.text(line, margin + 10, textY);
      textY += 10;
    } else {
      pdf.setFont('helvetica', 'bold');
      pdf.text(line, margin + 10, textY);
      textY += 10;
    }
  });
  
  // Add disclaimer page
  yPosition = addNewPage('DISCLAIMER');
  
  pdf.setTextColor(colors.dark[0], colors.dark[1], colors.dark[2]);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('IMPORTANT DISCLAIMER', pageWidth/2, yPosition, { align: 'center' });
  
  yPosition += 20;
  
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  
  const disclaimer = [
    "This JoSAA prediction report is based on historical cutoff data and statistical analysis. While we strive for accuracy, actual admission depends on various factors:",
    "",
    "• Current year competition and number of applicants",
    "• Seat matrix changes by institutes",
    "• Policy changes in reservation or counseling process",
    "• Actual choice filling by other candidates",
    "",
    "RECOMMENDATIONS:",
    "• Use this report as guidance, not as a guarantee",
    "• Verify current information from official JoSAA website",
    "• Consult with counselors and seniors",
    "• Keep backup options ready",
    "",
    "The predictions are categorized as High, Medium, and Low chance based on your rank position relative to previous year cutoffs. These probabilities are estimates and actual results may vary.",
    "",
    "For the most current information, always refer to:",
    "• Official JoSAA website (josaa.nic.in)",
    "• Institute websites",
    "• Official counseling notifications"
  ];
  
  let disclaimerY = yPosition;
  disclaimer.forEach(line => {
    if (line === "") {
      disclaimerY += 8;
    } else if (line.startsWith("•")) {
      pdf.text(line, margin + 15, disclaimerY);
      disclaimerY += 8;
    } else if (line.includes(":")) {
      pdf.setFont('helvetica', 'bold');
      pdf.text(line, margin + 10, disclaimerY);
      pdf.setFont('helvetica', 'normal');
      disclaimerY += 10;
    } else {
      const lines = pdf.splitTextToSize(line, contentWidth - 20);
      pdf.text(lines, margin + 10, disclaimerY);
      disclaimerY += lines.length * 6;
    }
  });
  
  // Add signature
  disclaimerY += 20;
  pdf.setFont('helvetica', 'bold');
  pdf.text('Grad Vikalp Team', margin + 10, disclaimerY);
  
  pdf.setFont('helvetica', 'normal');
  pdf.text('Your JoSAA Counseling Partner', margin + 10, disclaimerY + 10);
  
  // Save the PDF
  const fileName = `${data.studentInfo.name.replace(/\s+/g, '_')}_JoSAA_Predictions_${new Date().getFullYear()}.pdf`;
  pdf.save(fileName);
};